import { useContext, useEffect, useState } from "react";
import {
  Deployment,
  ServiceStack,
  useDeployMyCdnStackMutation,
  useGetMyCdnServiceStackStatusQuery,
  useGetMyEnvFeaturesStackStatusQuery,
  useGetMyGitConnectionsQuery,
  useRemoveMyCdnStackMutation,
  useWhoAmIQuery,
} from "../../../services/backend_gateway/__generated__/backend_gateway-types";
import { DeployCdnServiceComponent } from "./component";
import { BgwContext } from "../../../contexts/backend_gateway/context";
import { AlertType } from "../../../contexts/alerts/type";
import { AlertsContext } from "../../../contexts/alerts/context";

export const DeployCdnServiceContainer = (props: {
  deployment: Deployment;
  stack: ServiceStack;
}) => {
  const { deployment, stack } = props;
  const [queryError, setQueryError] = useState<string[]>([]);

  const removeQueryError = (i: number) => {
    setQueryError((e) => {
      const newErrors = [...e];
      newErrors.splice(i, 1);
      return newErrors;
    });
  };

  const [mutationInProgress, setMutationInProgress] = useState<boolean>(false);

  const { bgwService } = useContext(BgwContext);
  const { addAlert } = useContext(AlertsContext);

  const { data: getMyGitConnectionsData, loading: getMyGitConnectionsLoading } =
    useGetMyGitConnectionsQuery({
      client: bgwService.getClient(),
      variables: {
        deploymentId: deployment.id,
      },
      onError: (error) => {
        if (error?.message !== undefined) {
          addAlert({
            text: error.message!,
            type: AlertType.WARNING,
          });
        }
      },
    });

  const {
    data: getMyEnvFeaturesStackStatusData,
    loading: getMyEnvFeaturesStackStatusLoading,
  } = useGetMyEnvFeaturesStackStatusQuery({
    client: bgwService.getClient(),
    variables: {
      deploymentId: deployment.id,
    },
    onError: (error) => {
      if (error?.message !== undefined) {
        addAlert({
          text: error.message!,
          type: AlertType.WARNING,
        });
      }
    },
  });

  const envFeaturesParams = JSON.parse(
    getMyEnvFeaturesStackStatusData?.getMyEnvFeaturesStackStatus
      ?.parametersJSON || "{}"
  );
  const hasFullDomainName =
    envFeaturesParams.DeploymentDomainName !== "" ||
    deployment.fullDomainName !== "";
  // const envLogRetentionDays =
  //   parseInt(envFeaturesParams.LogRetentionDays) || 365;
  // const envVpcEnabled = envFeaturesParams.VpcEnabled === "True";

  const hasAuthentication = envFeaturesParams.CognitoEnabled === "True";
  const hasGoogleAuthentication = envFeaturesParams.GoogleClientId !== "";

  const {
    data: getMyCdnServiceStackStatusData,
    loading: getMyCdnServiceStackStatusLoading,
    error: getMyCdnServiceStackStatusError,
    refetch: getMyCdnServiceStackStatusRefetch,
  } = useGetMyCdnServiceStackStatusQuery({
    client: bgwService.getClient(),
    variables: {
      deploymentId: deployment.id,
      pipelineId: stack.id,
    },
  });

  useEffect(() => {
    if ((getMyCdnServiceStackStatusError?.message || null) !== null) {
      setQueryError((e) => [...e, getMyCdnServiceStackStatusError!.message]);
    }
  }, [getMyCdnServiceStackStatusError]);

  const [deployMyCdnStackMutation, { loading: deployMyCdnStackLoading }] =
    useDeployMyCdnStackMutation({
      client: bgwService.getClient(),
    });

  const deployCdnService = (
    gitConnectionArn: string,
    gitRepositoryId: string,
    gitBranch: string,
    buildScriptPath: string,
    buildFolderPath: string,
    buildspecPath: string,
    buildtimeJson: string,
    webHostname: string,
    altFullDomainName: string,
    altCertificateArn: string,
    oidcEnabled: boolean,
    oidcProviderName: string,
    oidcUrl: string,
    oidcClientId: string,
    oidcClientSecret: string,
    oidcThumbprint: string,
    googleEnabled: boolean,
    callbackUri: string,
    logoutUri: string,
    localhostUrl: string,
    wafEnabled: boolean,
    emailPasswordEnabled: boolean
  ) => {
    deployMyCdnStackMutation({
      variables: {
        stackDeploymentInput: {
          id: stack.id,
          deploymentId: deployment.id,
          gitConnectionArn,
          gitRepositoryId,
          gitBranch,
          buildScriptPath: buildScriptPath === "" ? null : buildScriptPath,
          buildFolderPath: buildFolderPath === "" ? null : buildFolderPath,
          buildspecPath: buildspecPath === "" ? null : buildspecPath,
          buildtimeJson,
          webHostname,
          altFullDomainName:
            altFullDomainName === "" ? null : altFullDomainName,
          altCertificateArn:
            altCertificateArn === "" ? null : altCertificateArn,
          oidcEnabled,
          oidcProviderName: oidcProviderName === "" ? null : oidcProviderName,
          oidcUrl,
          oidcClientId,
          oidcClientSecret: oidcClientSecret === "" ? null : oidcClientSecret,
          oidcThumbprint,
          googleEnabled,
          callbackUri,
          logoutUri,
          localhostUrl,
          wafEnabled,
          emailPasswordEnabled,
        },
      },
      // update(cache, { data }) {
      //   cache.modify({
      //     fields: {
      //       getMyCdnServiceStackStatus(existingnewStackStatuses = []) {
      //         const newStackStatuskRef = cache.writeFragment({
      //           data: data?.deployMyCdnStack,
      //           fragment: gql`
      //             fragment NewStackStatus on StackStatus {
      //               status
      //               parametersJSON
      //             }
      //           `,
      //         });
      //         return [...existingnewStackStatuses, newStackStatuskRef];
      //       },
      //     },
      //   });
      // },
      onCompleted: (data) => {
        getMyCdnServiceStackStatusRefetch();
      },
      onError: (error) => {
        setQueryError((e) => [
          ...e,
          `Failed to deploy stack to ${deployment.id}.`,
        ]);
        console.error(error);
      },
    });
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (
        (
          getMyCdnServiceStackStatusData?.getMyCdnServiceStackStatus?.status ||
          ""
        ).indexOf("IN_PROGRESS") > -1
      ) {
        getMyCdnServiceStackStatusRefetch();
      }
    }, 8000);
    return () => clearInterval(intervalId);
  }, [getMyCdnServiceStackStatusData, getMyCdnServiceStackStatusRefetch]);

  const [removeMyCdnStack, { loading: removeMyCdnStackLoading }] =
    useRemoveMyCdnStackMutation({
      client: bgwService.getClient(),
    });

  const removeCdnService = () => {
    setMutationInProgress(true);
    removeMyCdnStack({
      variables: {
        stackId: null,
        deploymentId: deployment.id,
        pipelineId: stack.id,
      },
      onCompleted: () => {
        getMyCdnServiceStackStatusRefetch();
        setMutationInProgress(false);
      },
      onError: (error) => {
        setQueryError((e) => [
          ...e,
          `Failed to remove stack from ${deployment.id}.`,
        ]);
        console.error(error);
        setMutationInProgress(false);
      },
    });
  };

  const { data: whoAmIData, loading: whoAmILoading } = useWhoAmIQuery({
    client: bgwService.getClient(),
    onError: (error) => {
      if (error?.message !== undefined) {
        addAlert({
          text: error.message!,
          type: AlertType.WARNING,
        });
      }
    },
  });
  const amIRoot = whoAmIData?.whoAmI?.role === "ROOT" || false;

  return (
    <DeployCdnServiceComponent
      loading={
        getMyGitConnectionsLoading ||
        getMyEnvFeaturesStackStatusLoading ||
        getMyCdnServiceStackStatusLoading ||
        deployMyCdnStackLoading ||
        removeMyCdnStackLoading ||
        mutationInProgress ||
        whoAmILoading
      }
      stack={stack}
      deployment={deployment}
      stackStatus={
        getMyCdnServiceStackStatusData?.getMyCdnServiceStackStatus || null
      }
      gitConnectionArns={getMyGitConnectionsData?.getMyGitConnections || []}
      queryError={queryError}
      removeQueryError={removeQueryError}
      deployCdnService={deployCdnService}
      refetchStackStatus={() => {
        getMyCdnServiceStackStatusRefetch();
      }}
      removeCdnService={removeCdnService}
      amIRoot={amIRoot}
      hasFullDomainName={hasFullDomainName}
      fullDomainName={
        envFeaturesParams.DeploymentDomainName ||
        deployment.fullDomainName ||
        null
      }
      hasAuthentication={hasAuthentication}
      hasGoogleAuthentication={hasGoogleAuthentication}
    />
  );
};
